/**
 * Data-bag matrix transform (R5 Gumball + R2c reuse).
 *
 * Applies a THREE.Matrix4 to every geometric point field of an entity
 * data bag: raw drafting keys, mesh payloads and NURBS kernel payloads
 * (curve points, surface CV, tessellation caches regenerated by the
 * caller via refreshKernelTessellation). Pure data mutation — the undo
 * wrapper stays in entityOps so every transform is one history step.
 */

import * as THREE from 'three';
import type { Pt3 } from '../commands/geometryMath';
import type { NurbsCurveData, NurbsSurfaceData } from '../geometry/nurbs';
import { sampleCurve, tessellateSurface } from '../geometry/nurbs';

export function transformDataBag(data: Record<string, any>, matrix: THREE.Matrix4): Record<string, any> {
    const v = new THREE.Vector3();
    const tf = (p: number[]): number[] => {
        v.set(p[0], p[1], p[2] ?? 0);
        v.applyMatrix4(matrix);
        return [v.x, v.y, v.z];
    };
    ['start', 'end', 'position', 'center'].forEach((k) => { if (Array.isArray(data[k])) data[k] = tf(data[k]); });
    ['vertices', 'controlPoints'].forEach((k) => { if (Array.isArray(data[k])) data[k] = (data[k] as Pt3[]).map(tf); });
    if (data.curve && Array.isArray(data.curve.points)) {
        data.curve = { ...data.curve, points: (data.curve.points as Pt3[]).map(tf) };
    }
    if (data.surface && Array.isArray(data.surface.cv)) {
        data.surface = { ...data.surface, cv: (data.surface.cv as Pt3[][]).map((row) => row.map(tf)) };
    }
    if (data.cage && Array.isArray(data.cage.vertices)) {
        data.cage = { ...data.cage, vertices: (data.cage.vertices as Pt3[]).map(tf) };
    }
    return data;
}

/** Collect representative world points of any entity (for bounds/gizmo). */
export function dataBagPoints(data: Record<string, any>): Pt3[] {
    const pts: Pt3[] = [];
    ['start', 'end', 'position', 'center'].forEach((k) => { if (Array.isArray(data[k]) && typeof data[k][0] === 'number') pts.push(data[k] as Pt3); });
    ['vertices', 'controlPoints'].forEach((k) => { if (Array.isArray(data[k])) (data[k] as number[][]).forEach((p) => typeof p[0] === 'number' && pts.push(p as Pt3)); });
    if (data.curve && Array.isArray(data.curve.points)) (data.curve.points as number[][]).forEach((p) => pts.push(p as Pt3));
    if (data.cage && Array.isArray(data.cage.vertices)) (data.cage.vertices as number[][]).forEach((p) => pts.push(p as Pt3));
    const tess = data.tessellation;
    if (tess && !Array.isArray(tess) && Array.isArray(tess.vertices)) (tess.vertices as number[][]).forEach((p) => pts.push(p as Pt3));
    if (Array.isArray(tess)) (tess as number[][]).forEach((p) => pts.push(p as Pt3));
    return pts;
}

/**
 * Regenerate NURBS kernel tessellation caches after a transformDataBag pass
 * (curve/surface control points moved → stale display mesh). Lives here so
 * every cross-workbench edit path (Gumball, edit router, command runners)
 * shares one definition; runners/lib re-exports it.
 */
export function refreshKernelTessellation(data: Record<string, any>): Record<string, any> {
    if (data.curve) data.tessellation = sampleCurve(data.curve as NurbsCurveData, 64);
    else if (data.surface) data.tessellation = tessellateSurface(data.surface as NurbsSurfaceData, 24);
    return data;
}
